import React, { useState, useEffect } from 'react';
import Dropzone from 'react-dropzone';
import styled from 'styled-components';

import { colors } from 'helpers/ColorHelper';
import { media, sizes } from 'helpers/MediaHelper';
import { getTranslatedString } from 'helpers/i18n';

import { MdPhotoCamera as FileDropIcon } from 'react-icons/md';
import { MdClose as CloseIcon } from 'react-icons/md';

const StyledOuterContainer = styled.div``;

const StyledDropzone = styled(Dropzone)`
  width: 100%;
  border: none;
  padding: 0;
  cursor: pointer;
  margin-right: 20px;
`;

const StyledContainer = styled.div`
  height: 40px;
  display: flex;
  align-items: center;
  border: ${(props) =>
    props.isDragActive ? `1px solid ${colors.LIGHT_BLUE_GREY}` : `1px dashed ${colors.GRAY}`};
  border-radius: 8px;
  margin: 10px 0px;
  justify-content: ${(props) => (props.isDragActive ? 'center' : 'auto')};
  background-color: ${(props) => (props.isDragActive ? colors.LIGHT_BLUE_GREY : colors.WHITE)};
  cursor: pointer;
`;

const StyledDropzoneText = styled.p`
  display: flex;
  flex-direction: 'row';
  align-items: center;
  line-height: 25px;
  margin: 10px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  justify-content: space-between;
  ${media.mobile`
    justify-content: flex-start;
  `};
`;

const StyledDropHereText = styled.span`
  display: flex;
  flex-direction: 'row';
  align-items: center;
  color: ${(props) => (props.isDragActive ? colors.PURE_BLUE : colors.SLATE_GREY)};
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  font-size: 16px;
`;

const StyledSpan = styled.span`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  display: inline-block;
  font-size: 16px;
`;

const StyledBrowseFileText = styled.span`
  color: ${colors.PURE_BLUE};
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  font-size: 16px;
`;

const StyledFileContainer = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px dashed #bdbdbd;
  background-color: ${colors.LIGHT_GREY};
`;

const StyledFileContainerHeader = styled.div`
  height: 30px;
  border-bottom: 1px dashed #bdbdbd;
  padding: 5px 15px;
`;

const StyledFileContainerBody = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin-top: 5px;
  padding: 8px 15px;
`;

const StyledThumb = styled.div`
  display: inline-flex;
  box-sizing: border-box;
  margin-right: 10px;
`;

const StyledThumbInner = styled.div`
  display: flex;
  min-width: 0;
  overflow: hidden;
`;

const StyledThumbInnerLink = styled.a`
  display: flex;
  min-width: 0;
  overflow: hidden;
`;

const StyledImg = styled.img`
  display: block;
  width: 48px; // auto
  height: 48px;
`;

const StyledRemoveButton = styled.button`
  display: flex;
  height: 20px;
  width: 20px;
  margin-top: -8px;
  margin-left: -8px;
  border: none;
  padding: 0;
`;

const StyledDropIcon = styled(FileDropIcon)`
  font-size: 24px;
  margin: 2px 10px 2px 0;
`;

const StyledCloseIcon = styled(CloseIcon)`
  border: 1px solid white;
  font-size: 18px;
  background-color: white;
  border-radius: 50%;
  height: 20px;
`;

const StyledError = styled.div`
  color: red;
  margin-top: 10px;
`;

const InputFile = ({
  contentKey,
  newFiles,
  oldFiles = [],
  data,
  onChange,
  onDropVerbiage,
  dropFileVerbiage,
  browseVerbiage,
  acceptedFiles,
  rejectFileError,
  maxSize = 24000000,
  readOnly,
  ...rest
}) => {
  const [files, setFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState(oldFiles);
  const [filesUploaded, setFilesUploaded] = useState(oldFiles.length);
  const isMobile = window.matchMedia(`(max-width: ${sizes.mobile}px)`).matches;

  useEffect(() => {
    if (data) {
      setUploadedFiles(data);
      if (files.length > 0 || data.some((d) => d.keep === true)) setFilesUploaded(true);
    }
  }, [data, files.length]);

  useEffect(() => {
    setUploadedFiles(oldFiles);
    setFilesUploaded(oldFiles.length);
  }, [oldFiles]);

  const onDrop = (acceptedFiles) => {
    let newFiles = [...files];
    newFiles.push(...acceptedFiles);
    setFilesUploaded(true);
    setFiles(newFiles);
    if (onChange) {
      onChange(newFiles, uploadedFiles);
    }
  };

  const removeFile = (file) => () => {
    let newFiles = [...files];
    let index = newFiles.indexOf(file);
    newFiles.splice(index, 1);
    setFiles(newFiles);
    if (files.length === 0 || uploadedFiles.every((file) => file.keep === false))
      setFilesUploaded(false);
    if (onChange) {
      onChange(newFiles, uploadedFiles);
    }
  };

  const removeUploadedFiles = (file) => () => {
    let newFiles = [...uploadedFiles];
    let index = newFiles.indexOf(file);
    newFiles[index].keep = false;
    setUploadedFiles(newFiles);
    if (files.length === 0 || uploadedFiles.every((file) => file.keep === false))
      setFilesUploaded(false);
    if (onChange) {
      onChange(files, newFiles);
    }
  };

  return (
    <StyledOuterContainer {...rest}>
      <StyledContainer filesUploaded={filesUploaded}>
        <StyledDropzone onDrop={onDrop} accept={acceptedFiles} maxSize={maxSize}>
          {({ isDragActive, rejectedFiles }) => (
            <React.Fragment>
              {isDragActive ? (
                <StyledDropHereText isDragActive={isDragActive}>
                  {onDropVerbiage}
                </StyledDropHereText>
              ) : (
                <StyledDropzoneText filesUploaded={filesUploaded}>
                  <StyledDropHereText filesUploaded={filesUploaded}>
                    <StyledDropIcon />
                    {!isMobile && <StyledSpan>{dropFileVerbiage}</StyledSpan>}
                  </StyledDropHereText>
                  <StyledBrowseFileText>{browseVerbiage}</StyledBrowseFileText>
                </StyledDropzoneText>
              )}
              {rejectedFiles.length > 0 && <StyledError>{rejectFileError}</StyledError>}
            </React.Fragment>
          )}
        </StyledDropzone>
      </StyledContainer>
      {files.length > 0 && <UnsavedFiles files={files} removeFile={removeFile} />}
      {uploadedFiles.length > 0 && (
        <SavedFiles
          uploadedFiles={uploadedFiles}
          removeUploadedFiles={removeUploadedFiles}
          readOnly={readOnly}
        />
      )}
    </StyledOuterContainer>
  );
};

const UnsavedFiles = ({ files, removeFile }) => (
  <StyledFileContainer>
    <StyledFileContainerHeader>
      <StyledSpan>
        {getTranslatedString('landlordPortal.maintenance.tickets.unsavedItems')}
      </StyledSpan>
    </StyledFileContainerHeader>
    <StyledFileContainerBody>
      {files.map((file) => (
        <StyledThumb key={`uploaded-file-btn-${file.id}`}>
          <StyledThumbInner key={`uploaded-file-name-${file.id}`}>
            <StyledImg src={file.preview} />
          </StyledThumbInner>
          <StyledRemoveButton
            key={`uploaded-file-remove-${file.id}`}
            type="button"
            onClick={removeFile(file)}
          >
            <StyledCloseIcon />
          </StyledRemoveButton>
        </StyledThumb>
      ))}
    </StyledFileContainerBody>
  </StyledFileContainer>
);

const SavedFiles = ({ uploadedFiles, removeUploadedFiles, readOnly }) => (
  <StyledFileContainer>
    <StyledFileContainerHeader>
      <StyledSpan>
        {getTranslatedString('landlordPortal.maintenance.tickets.savedItems')}
      </StyledSpan>
    </StyledFileContainerHeader>
    <StyledFileContainerBody>
      {uploadedFiles.map((file) =>
        file.keep === false ? null : (
          <StyledThumb key={`file-btn-${file.id}`}>
            <StyledThumbInnerLink
              key={`file-name-${file.id}`}
              href={file.src}
              target="_blank"
              rel="noopener noreferrer"
            >
              <StyledImg src={file.src} />
            </StyledThumbInnerLink>
            {!readOnly && (
              <StyledRemoveButton
                key={`file-remove-btn-${file.id}`}
                type="button"
                onClick={removeUploadedFiles(file)}
              >
                <StyledCloseIcon />
              </StyledRemoveButton>
            )}
          </StyledThumb>
        )
      )}
    </StyledFileContainerBody>
  </StyledFileContainer>
);

export default InputFile;
